/**
 * Sentry error monitoring wrapper.
 * All exports are no-ops until initSentry() successfully loads @sentry/react.
 * This allows the app to build and run without @sentry/react installed.
 *
 * To activate: npm install @sentry/react && set VITE_SENTRY_DSN in .env
 */

let Sentry = null

export async function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  try {
    Sentry = await import('@sentry/react')
  } catch {
    // @sentry/react not installed — monitoring disabled
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    sampleRate: 1.0,
    tracesSampleRate: 0.1,
    enabled: import.meta.env.PROD,
    beforeSend(event) {
      const msg = event.exception?.values?.[0]?.value || ''
      // Skip LockManager timeouts (handled by auto-reload in main.jsx)
      if (msg.includes('Navigator LockManager') || (msg.includes('lock') && msg.includes('timed out'))) {
        return null
      }
      // Skip stale chunk errors (handled by ErrorBoundary auto-reload)
      if (msg.includes('Failed to fetch dynamically imported module') || msg.includes('Loading chunk')) {
        return null
      }
      return event
    },
  })
}

export function setSentryUser(userId, email, subscriptionTier) {
  if (!Sentry) return
  Sentry.setUser(userId ? { id: userId, email } : null)
  if (subscriptionTier) {
    Sentry.setTag('subscription_tier', subscriptionTier)
  }
}

export function captureException(error, context) {
  if (!Sentry) return
  Sentry.captureException(error, context ? { extra: context } : undefined)
}
